import { clsx } from 'clsx';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { OfferFormFirst } from '../../../components/offer-form/offer-form-first';
import styles from './offer-screen.module.scss';
import { BackButton } from '@components/back-button/back-button';
import { OfferFormSecond, PartnerOfferingForm } from '@components/offer-form/offer-form-second';
import { dataprotectionText } from '@components/offer-form/form-steps/submit-section';
import { OfferFormWrapper } from '@components/offer-form/form-wrapper';
import { WizardAction, WizardState, WizardActionType } from '@hooks/utils/wizard-types';

const ResultBackButton: React.FC<{
  previousStep: () => void;
  className?: string;
  label: string;
}> = ({ previousStep, className, label }) => (
  <BackButton
    onClick={previousStep}
    label={label}
    btnClass={clsx('margin-bottom--m', styles.backBtn, className)}
  />
);

type OfferScreenProps = {
  headline: PvWizardSectionOffer['headline'];
  benefits: PvWizardSectionOffer['benefits'];
  partners: PvWizardSectionOffer['partners'];
  formHeadlines: PvWizardSectionOffer['formHeadlines'];
  formModals: PvWizardSectionOffer['formModals'];
  selectedPartnersContent: PvWizardSectionOffer['selectedPartnersContent'];
  hasVehicle: boolean;
  previousStep: () => void;
  toggleDisclaimerModal: () => void;
  result: PvWizardResultSectionOffer;
  submitForm: (data: FormDataType, wizardData: WizardDataType) => void;
  modalContainerRef: React.RefObject<HTMLDivElement>;
  dispatch: (action: WizardAction) => void;
  partnerOfferingForm: WizardState['partnerOfferingForm'];
  scrollIntoView: () => void;
};

export const OfferScreen: React.FC<OfferScreenProps> = ({
  headline,
  benefits,
  partners,
  formHeadlines,
  formModals,
  selectedPartnersContent,
  hasVehicle,
  previousStep,
  toggleDisclaimerModal,
  result,
  submitForm,
  modalContainerRef,
  dispatch,
  partnerOfferingForm,
  scrollIntoView
}) => {
  const [offerScreenPage, setOfferScreenPage] = useState(1);

  const form = useForm<PartnerOfferingForm>({
    defaultValues: partnerOfferingForm || {
      partners: [],
      allPartners: false,
      title: '',
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      street: '',
      houseNr: '',
      zipcode: '',
      city: '',
      dataProtection: Object.values(dataprotectionText).join('')
    },
    mode: 'onSubmit'
  });

  const handleBackButton = () => {
    previousStep();
    dispatch({
      type: WizardActionType.SET_FORM_DATA,
      payload: { partnerOfferingForm: form.getValues() },
      isWizardClickedThrough: false
    });
  };

  const navigateToPage = (page: number) => {
    setOfferScreenPage(page);
  };

  return (
    <FormProvider {...form}>
      <OfferFormWrapper
        solarPartnersRequestData={result.solarPartnersRequestData}
        submit={submitForm}
      >
        {offerScreenPage === 1 && (
          <>
            <ResultBackButton previousStep={handleBackButton} label="Zurück zum Solarrechner" />
            <OfferFormFirst
              headline={headline}
              toggleDisclaimerModal={toggleDisclaimerModal}
              partners={partners}
              result={result}
              benefits={benefits}
              hasVehicle={hasVehicle}
              navigateToPage={navigateToPage}
              scrollIntoView={scrollIntoView}
            />
            <ResultBackButton
              previousStep={handleBackButton}
              className={styles.backBtnEnd}
              label="Zurück zum Solarrechner"
            />
          </>
        )}

        {offerScreenPage === 2 && (
          <>
            <ResultBackButton
              previousStep={() => {
                navigateToPage(1);
                scrollIntoView();
              }}
              className={styles.backBtnEnd}
              label="Zurück zur vorigen Seite"
            />
            <OfferFormSecond
              formHeadlines={formHeadlines}
              formModals={formModals}
              modalContainerRef={modalContainerRef}
              selectedPartnersContent={selectedPartnersContent}
            />
            <ResultBackButton
              previousStep={() => {
                navigateToPage(1);
                scrollIntoView();
              }}
              className={styles.backBtnEnd}
              label="Zurück zur vorigen Seite"
            />
          </>
        )}
      </OfferFormWrapper>
    </FormProvider>
  );
};
